import { injectable } from 'inversify';
import type { ICommand } from '../interfaces/Command';
import { CommandCompound } from '../commands/CommandCompound';
import { action, makeObservable, observable } from 'mobx';

@injectable()
export class CommandHistory {
  @observable
  private _undoStack: Array<ICommand> = [];

  @observable
  private _redoStack: Array<ICommand> = [];

  @observable
  private _currentCommandCompound: CommandCompound | null = null;

  public constructor() {
    makeObservable(this);
  }

  @action
  public append(command: ICommand): void {
    if (this.canRedo()) {
      this._redoStack = [];
    }
    if (this._currentCommandCompound) {
      this._currentCommandCompound.add(command);
    } else {
      this._undoStack.push(command);
    }
  }

  @action
  public getForUndo(): ICommand | undefined {
    if (!this.canUndo()) {
      return undefined;
    }
    if (this._currentCommandCompound && !this._currentCommandCompound.isFinalized) {
      const commands = this._currentCommandCompound.getCommands();
      if (commands.length > 0) {
        const command = commands.pop();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this._redoStack.push(command!);
        return command;
      }
      // If no more commands in current Compound, pop it and continue
      this._currentCommandCompound = null;
    }
    const command = this._undoStack.pop();
    if (command) {
      this._redoStack.push(command);
      if (
        'isCompound' in command &&
        (command as CommandCompound).isCompound &&
        !(command as CommandCompound).isFinalized
      ) {
        this._currentCommandCompound = command as CommandCompound;
      }
    }
    return command;
  }

  @action
  public getForRedo(): ICommand | undefined {
    const command = this._redoStack.pop();
    if (command) {
      if (this._currentCommandCompound) {
        this._currentCommandCompound.add(command);
      } else {
        this._undoStack.push(command);
        if (
          'isCompound' in command &&
          (command as CommandCompound).isCompound &&
          !(command as CommandCompound).isFinalized
        ) {
          this._currentCommandCompound = command as CommandCompound;
        }
      }
    }
    return command;
  }

  public canUndo(): boolean {
    if (this._currentCommandCompound) {
      return this._currentCommandCompound.getCommands().length > 0 || this._undoStack.length > 0;
    }
    return this._undoStack.length > 0;
  }

  public canRedo(): boolean {
    return this._redoStack.length > 0;
  }

  @action
  public startCommandCompound(): void {
    this._currentCommandCompound = new CommandCompound();
    this.clearRedoStack();
  }

  @action
  public finishCommandCompound(): void {
    if (this._currentCommandCompound) {
      this._currentCommandCompound.finalize();
      this._undoStack.push(this._currentCommandCompound);
      this._currentCommandCompound = null;
    }
  }

  @action
  public clearRedoStack(): void {
    this._redoStack = [];
  }
}
